// web/src/tiferet/TourRunner.tsx — סיור של תפארת על המסך (29.09). מקביל לסיורים של "היועץ" ברשגד.
// 🔑 כל שלב: מאירים את החלק במסך (שאר המסך מתעמעם) ובועה עם הסבר + קול. ⛔ חוסמים את המסך — בשלבי בדיקה היא צריכה
//    לנסות בעצמה, ולכן ההאפלה "שקופה" ללחיצות.
// 🔑 סלקטור: כמה אפשרויות מופרדות בפסיק — הראשונה שנראית. optional ⇒ לא מופיע = מדלגים · אחרת הבועה במרכז והסבר.
// 🔑 מקלדת: Esc סגירה · ← הבא · → הקודם (עברית: קדימה = שמאלה).
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { components } from "../api/gen/tf";
import { Glow } from "./Glow";
import * as voice from "./voice";

type Tour = components["schemas"]["Tour"];
export type Step = components["schemas"]["TourStep"];

export function findTarget(sel: string): HTMLElement | null {
  for (const s of sel.split(",").map((x) => x.trim()).filter(Boolean)) {
    let els: NodeListOf<HTMLElement>;
    try { els = document.querySelectorAll<HTMLElement>(s); } catch { continue; }
    for (const el of Array.from(els)) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== "hidden") return el;
    }
  }
  return null;
}

/** השלבים שאפשר להציג עכשיו: optional שלא מופיע במסך — יוצא. */
export function playable(steps: Step[]): Step[] {
  return steps.filter((s) => !s.optional || findTarget(s.sel));
}

type Box = { top: number; left: number; width: number; height: number };
const PAD = 6;

function useVoiceState() {
  const [, force] = useState(0);
  useEffect(() => voice.subscribe(() => force((n) => n + 1)), []);
  return { speaking: voice.speaking(), muted: voice.isMuted() };
}

export function TourRunner({ tour, steps, start = 0, hasVoice = true, point = false, onClose, onCheck, onDeep }: {
  tour: Tour;
  steps: Step[];
  start?: number;
  /** 🆕 29.09 לילה — הצבעה אחת מתוך שיחה (הכישור highlight): בלי מונה, "הבנתי" במקום "סיום". */
  point?: boolean;
  /** יש קול בשרת (מפתח + לא כבוי). */
  hasVoice?: boolean;
  /** finished = הגיעה עד הסוף. */
  onClose: (finished: boolean, index: number) => void;
  onCheck: (step: Step, ok: boolean, index: number) => void;
  /** 🆕 27.10 — סיור המערכת: "סיור מפורט בעמוד הזה" (step.deep). */
  onDeep?: (tool: string) => void;
}) {
  const [i, setI] = useState(start);
  const [box, setBox] = useState<Box | null>(null);
  const [missing, setMissing] = useState(false);
  const bubble = useRef<HTMLDivElement>(null);
  const [bsize, setBsize] = useState({ w: 360, h: 220 });
  const { speaking, muted } = useVoiceState();
  const step = steps[i];
  const loc = useLocation();
  const nav = useNavigate();
  const found = useRef(false);
  const since = useRef(0);

  // ── מציאת היעד + גלילה אליו ──
  useEffect(() => {
    if (!step) return;
    // 🆕 27.10 — סיור המערכת: השלב יושב במסך אחר ⇒ עוברים אליו, והיעד נמצא כשהמסך נטען (המעקב למטה).
    if (step.go && loc.pathname !== step.go) nav(step.go);
    found.current = false;
    since.current = performance.now();
    setMissing(false);
    if (step.click && !findTarget(step.sel)) findTarget(step.click)?.click();
    // קול: אחרי נגיעה בדף — לבד. ההקלטה של השלב הבא נטענת מראש.
    if (hasVoice && voice.canAutoplay()) void voice.play(step.voice);
    if (hasVoice) void voice.load(steps[i + 1]?.voice);
  }, [i, step]);                                          // eslint-disable-line react-hooks/exhaustive-deps

  // ── מעקב אחרי המיקום (גלילה · שינוי גודל · תוכן שנטען) ──
  useEffect(() => {
    if (!step) return;
    let raf = 0;
    const tick = () => {
      const el = findTarget(step.sel);
      if (el && !found.current) {
        // היעד הופיע (מיד, או אחרי מעבר מסך / לחיצה) ⇒ פעם אחת: גלילה אליו.
        found.current = true;
        setMissing(false);
        const r0 = el.getBoundingClientRect();
        if (r0.top < 70 || r0.bottom > innerHeight - 40) el.scrollIntoView({ block: r0.height > innerHeight * 0.6 ? "start" : "center", behavior: "smooth" });
      } else if (!el && !found.current && performance.now() - since.current > (step.go ? 2500 : step.click ? 650 : 400)) {
        setMissing(true);
      }
      if (el) {
        const r = el.getBoundingClientRect();
        const top = Math.max(4, r.top - PAD), bottom = Math.min(innerHeight - 4, r.bottom + PAD);
        const next = { top, left: Math.max(4, r.left - PAD), width: Math.min(innerWidth - 8, r.width + PAD * 2), height: Math.max(0, bottom - top) };
        setBox((b) => (b && Math.abs(b.top - next.top) < 1 && Math.abs(b.left - next.left) < 1 && Math.abs(b.width - next.width) < 1
          && Math.abs(b.height - next.height) < 1 ? b : next));
      } else setBox(null);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step]);

  useLayoutEffect(() => {
    const b = bubble.current;
    if (b) setBsize((s) => (Math.abs(s.w - b.offsetWidth) < 2 && Math.abs(s.h - b.offsetHeight) < 2 ? s : { w: b.offsetWidth, h: b.offsetHeight }));
  });

  // ── מקלדת ──
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
      if (e.key === "Escape") { e.preventDefault(); close(false); }
      else if (!typing && e.key === "ArrowLeft" && step?.kind === "show") next();
      else if (!typing && e.key === "ArrowRight") prev();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  useEffect(() => () => voice.stop(), []);

  // 🆕 27.10 — שלב רשות שלא נמצא גם אחרי ההמתנה ⇒ מדלגים (בסיור המערכת השלבים לא נבדקים מראש).
  useEffect(() => {
    if (missing && step?.optional && !point) { if (i < steps.length - 1) setI(i + 1); else onClose(true, i); }
  }, [missing]);                                          // eslint-disable-line react-hooks/exhaustive-deps

  if (!step) return null;
  const last = i === steps.length - 1;
  function close(finished: boolean) { voice.stop(); onClose(finished, i); }
  function next() { if (last) close(true); else setI(i + 1); }
  function prev() { if (i > 0) setI(i - 1); }
  function check(ok: boolean) {
    onCheck(step!, ok, i);
    if (ok) next();                                         // ✗ — ההורה פותח הערה למוטי ועוצר את הסיור
  }

  // ── מיקום הבועה ──
  const narrow = innerWidth < 640;
  let pos: CSSProperties;
  if (narrow || !box || missing) {
    pos = narrow ? {} : { top: Math.max(16, (innerHeight - bsize.h) / 2), left: Math.max(16, (innerWidth - bsize.w) / 2) };
  } else {
    const below = box.top + box.height + 12, above = box.top - 12 - bsize.h;
    const top = below + bsize.h < innerHeight - 12 ? below : above > 12 ? above : Math.max(12, innerHeight - bsize.h - 12);
    const left = Math.min(Math.max(12, box.left + box.width / 2 - bsize.w / 2), innerWidth - bsize.w - 12);
    pos = { top, left };
  }
  const isCheck = step.kind === "check";
  // בטלפון — גיליון תחתון; כשהחלק המואר בחצי התחתון (למשל הכדור) — הגיליון עולה למעלה, כדי לא להסתיר אותו.
  const sheetTop = narrow && !!box && !missing && box.top + box.height / 2 > innerHeight / 2;

  return (
    <div className="tf-ui tf-tour" aria-live="polite">
      {box && !missing
        ? <div className="tf-spot" style={{ top: box.top, left: box.left, width: box.width, height: box.height }} aria-hidden="true" />
        : <div className="tf-dim" aria-hidden="true" />}
      <div ref={bubble} className={`tf-bubble card${narrow ? " is-sheet" : ""}${sheetTop ? " at-top" : ""}${isCheck ? " is-check" : ""}`} style={pos}
           role="dialog" aria-label={`סיור: ${tour.title}`}>
        <div className="tf-bubble-head">
          <Glow size="mini" speaking={!!speaking} />
          <span className="small muted">תפארת · {tour.title}{point ? "" : ` · ${i + 1} מתוך ${steps.length}`}</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn-quiet btn-sm" aria-label={muted ? "להפעיל קול" : "להשתיק"} title={muted ? "להפעיל קול" : "להשתיק"}
                  onClick={() => voice.setMuted(!muted)}>{muted ? "🔇" : "🔊"}</button>
          <button className="btn btn-quiet btn-sm" aria-label="סגירת הסיור" onClick={() => close(false)}>✕</button>
        </div>
        {isCheck && <span className="badge tf-check-badge">בדיקה</span>}
        <h3>{step.title}</h3>
        <p>{step.text}</p>
        {missing && !isCheck && <p className="muted small">{point ? "לא מצאתי את זה על המסך כרגע — אולי צריך לגלול או לפתוח לשונית." : "החלק הזה לא מופיע כרגע במסך, אז אני רק מספרת עליו."}</p>}
        {hasVoice && !muted && step.voice && (
          speaking === step.voice
            ? <button className="linklike small tf-listen" onClick={() => voice.stop()}>■ לעצור</button>
            : <button className="linklike small tf-listen" onClick={() => void voice.play(step.voice)}>▶ לשמוע</button>
        )}
        {isCheck ? (
          <div className="tf-nav">
            <button className="btn btn-primary btn-sm" onClick={() => check(true)}>✓ עובד</button>
            <button className="btn btn-secondary btn-sm" onClick={() => check(false)}>✗ משהו לא עובד</button>
            <span style={{ flex: 1 }} />
            {i > 0 && <button className="btn btn-quiet btn-sm" onClick={prev}>הקודם</button>}
            <button className="btn btn-quiet btn-sm" onClick={next}>{last ? "סיום" : "לדלג"}</button>
          </div>
        ) : (
          <div className="tf-nav">
            {i > 0 && <button className="btn btn-quiet btn-sm" onClick={prev}>הקודם</button>}
            <span style={{ flex: 1 }} />
            {step.deep && onDeep && (
              <button className="btn btn-secondary btn-sm" onClick={() => { voice.stop(); onDeep(step.deep!); }}>סיור מפורט בעמוד הזה</button>
            )}
            <button className="btn btn-primary btn-sm" onClick={next} autoFocus>{point ? "הבנתי" : last ? "סיום" : "הבא"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
